import React from 'react';
import { Activity, Workflow, FolderOpen, KeyRound, Download, Users, Check, ArrowUp, ShieldCheck, Boxes, Sparkles, Zap, Brain, LayoutGrid } from 'lucide-react';

// "Giga for ChatGPT" — one panel per selling point. Built on the Giga design
// tokens (Tailwind: brand / ink / sand / cream, fonts title/body/mono).
// Visuals are lightweight in-page mockups meant to be iterated on; drop real
// screenshots into the marked slots when they're ready.

const OPENAI_ICON = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg';
const GIGA_MARK = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png";

// ── Panel 01 · Connect far more tools ──────────────────────────────
// Share of the tools teams actually run on that each side can reach and act in.
const COVERAGE = [{
  category: 'CRM & sales',
  chatgpt: 8,
  giga: 98
}, {
  category: 'Engineering & dev',
  chatgpt: 12,
  giga: 96
}, {
  category: 'Finance & billing',
  chatgpt: 5,
  giga: 94
}, {
  category: 'Marketing & ads',
  chatgpt: 10,
  giga: 92
}, {
  category: 'Docs & storage',
  chatgpt: 28,
  giga: 99
}, {
  category: 'Support & comms',
  chatgpt: 9,
  giga: 95
}];

// ── Panel 02 · Sharing, done right ─────────────────────────────────
const GROUPS = [{
  name: 'Revenue',
  members: 14,
  tools: 'HubSpot · Gong · Stripe',
  access: 'Read & write'
}, {
  name: 'Engineering',
  members: 22,
  tools: 'GitHub · Linear · Sentry',
  access: 'Read & write'
}, {
  name: 'Finance',
  members: 5,
  tools: 'QuickBooks · Stripe · Excel',
  access: 'Read & write'
}, {
  name: 'Leadership',
  members: 6,
  tools: 'Everything',
  access: 'Read-only'
}];

// ── Panel 03 · Shared, inspectable memory ──────────────────────────
const MEMORY = [{
  tag: 'Pricing',
  fact: 'We price at 3× cost, with a $12k/yr floor. Discounts above 15% need Priya.'
}, {
  tag: 'Ownership',
  fact: 'Priya owns Northwind. Sam covers all EU accounts.'
}, {
  tag: 'Sales motion',
  fact: 'Renewals close 60–90 days out. We hold a 12-month liability cap.'
}];
const FEED = [{
  who: 'Giga',
  text: 'Reconciled June invoices — 312 of 314 matched, 1 duplicate flagged.',
  when: '2h ago'
}, {
  who: 'Giga',
  text: 'Published the launch post to WordPress from the launch doc.',
  when: '9:02am'
}, {
  who: 'Tom',
  text: 'Taught Giga our new discount policy.',
  when: 'Yesterday'
}];

// ── Panel 04 · More tools ──────────────────────────────────────────
const MORE_TOOLS = [{
  icon: Activity,
  name: 'Pulses',
  desc: 'Standing checks that watch your tools and surface what moved — to the right person, before anyone has to ask.'
}, {
  icon: Workflow,
  name: 'Workflows',
  desc: 'Multi-step jobs Giga runs end to end: pull, decide, act, report back. Once, on a schedule, or on a trigger.'
}, {
  icon: FolderOpen,
  name: 'Files & folders',
  desc: 'Contracts, decks, spreadsheets, exports. Dropped in once, organised and understood everywhere.'
}];

// ── Panel 05 · No lock-in ──────────────────────────────────────────
const NO_LOCK_IN = [{
  icon: Boxes,
  name: 'Works with other models',
  desc: "Routed to the best model for the job — GPT, Claude, Gemini. You're never tied to one lab."
}, {
  icon: OpenAIGlyph,
  name: 'Use outside of ChatGPT',
  desc: 'Prefer Slack, the web app, or the API? Same brain, wherever you reach it.'
}, {
  icon: KeyRound,
  name: 'Bring your own model',
  desc: 'Point Giga at your own keys or a private endpoint and keep everything on your terms.'
}, {
  icon: Download,
  name: 'Export everything',
  desc: 'Your memory, files, and history are yours. Export the lot, any time, no gatekeeping.'
}];

// ── Hero · the universe behind one message ─────────────────────────
// The user types @Giga + a query; behind that one line sits the whole system.
const UNIVERSE = [{
  icon: Users,
  name: 'Groups',
  desc: 'Teams with their own view of the brain and permissions.'
}, {
  icon: LayoutGrid,
  name: 'Spaces',
  desc: 'A dedicated workspace per project, client, or team.'
}, {
  icon: KeyRound,
  name: 'Credentials',
  desc: 'Managed, secure OAuth into every connected tool.'
}, {
  icon: Sparkles,
  name: 'Skills',
  desc: 'Reusable know-how Giga applies on request.'
}, {
  icon: Activity,
  name: 'Pulses',
  desc: 'Standing checks that watch your tools and alert.'
}, {
  icon: Zap,
  name: 'Power-ups',
  desc: 'Drop-in capabilities that extend what Giga can do.'
}, {
  icon: Workflow,
  name: 'Workflows',
  desc: 'Multi-step jobs Giga runs end to end.'
}, {
  icon: Boxes,
  name: 'Integrations',
  desc: '3,200+ tools — read and write, not just read.'
}, {
  icon: FolderOpen,
  name: 'Files & folders',
  desc: 'Everything it needs, dropped in and organised.'
}, {
  icon: Brain,
  name: 'Memory',
  desc: 'Shared, inspectable company knowledge.'
}];

// OpenAI logo used inline as a lucide-shaped "icon" for the lock-in panel.
function OpenAIGlyph({
  className
}: {
  className?: string;
}) {
  return <img src={OPENAI_ICON} alt="" className={`${className ?? ''} [filter:brightness(0)_invert(1)]`} data-id="element-9020" data-mp="21zfqev3myuh5" />;
}
function PulseDot() {
  return <span className="relative inline-flex w-[7px] h-[7px]" data-id="element-9021" data-mp="26vemz5z4bisn">
      <span className="absolute inset-0 rounded-full bg-brand animate-ping" data-id="element-9022" data-mp="1rz6ttg5qf718" />
      <span className="relative w-[7px] h-[7px] rounded-full bg-brand" data-id="element-9023" data-mp="1wys2wjem8vro" />
    </span>;
}

// Shared panel shell — numbered eyebrow, title, blurb, then a visual slot.
function Panel({
  n,
  eyebrow,
  title,
  blurb,
  dark = false,
  children
}: {
  n: string;
  eyebrow: string;
  title: string;
  blurb: string;
  dark?: boolean;
  children: React.ReactNode;
}) {
  return <section className={['rounded-[28px] border p-6 md:p-12 flex flex-col gap-8 md:gap-10', dark ? 'bg-ink border-white/10 text-white' : 'bg-white border-sand-200'].join(' ')} data-id="element-9024" data-mp="197w2wz00au9c">
      <div className="flex flex-col gap-3 max-w-[64ch]" data-id="element-9025" data-mp="109u7r02yevtz">
        <div className="flex items-center gap-2.5" data-id="element-9026" data-mp="14avy0rew7xjt">
          <span className={['font-mono text-[11px] tracking-[0.16em]', dark ? 'text-brand-lighter' : 'text-brand-dark'].join(' ')} data-id="element-9027" data-mp="14cqi63icblmu">{n}</span>
          <span className={['font-mono text-[11px] tracking-[0.16em] uppercase', dark ? 'text-white/50' : 'text-ink-faint'].join(' ')} data-id="element-9028" data-mp="3262ra6x2mwix">{eyebrow}</span>
        </div>
        <h2 className={['font-title font-extrabold text-2xl md:text-[34px] leading-[1.08] tracking-tight', dark ? 'text-white' : 'text-ink'].join(' ')} data-id="element-9029" data-mp="2x55a9xnss8j0">
          {title}
        </h2>
        <p className={['text-base leading-relaxed text-pretty', dark ? 'text-white/70' : 'text-ink-muted'].join(' ')} data-id="element-9030" data-mp="3pvdurw5t350r">
          {blurb}
        </p>
      </div>
      {children}
    </section>;
}
function CoverageBar({
  value,
  giga
}: {
  value: number;
  giga?: boolean;
}) {
  return <div className="flex items-center gap-3" data-id="element-9031" data-mp="3bvuoza0gw5rm">
      <div className="flex-1 h-2 rounded-full bg-sand-100 overflow-hidden" data-id="element-9032" data-mp="24iu8wksda3l5">
        <div className={['h-full rounded-full', giga ? 'bg-brand' : 'bg-ink-faint/50'].join(' ')} style={{
        width: `${value}%`
      }} data-id="element-9033" data-mp="24gpyrl3vk8cf" />
      </div>
      <span className={['font-mono text-[12px] w-9 text-right', giga ? 'text-brand-dark font-semibold' : 'text-ink-faint'].join(' ')} data-id="element-9034" data-mp="g07zmzvqpl0l">{value}%</span>
    </div>;
}
export function GigaForChatGPT() {
  return <div className="w-full bg-cream text-ink font-body" data-id="element-9035" data-mp="oytofcrt1w9t">
      <div className="max-w-[1120px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-20 md:pb-28 flex flex-col gap-6 md:gap-8" data-id="element-9036" data-mp="tql7l28mszpx">

        {/* ── Hero · an entire universe inside ChatGPT ──────────── */}
        <section className="flex flex-col items-center text-center gap-5 mb-2 md:mb-8" data-id="element-9037" data-mp="13or6rc31zrr3">
          <div className="flex items-center gap-2.5" data-id="element-9038" data-mp="13qlt8utz3rxa">
            <img src={OPENAI_ICON} alt="ChatGPT" className="w-5 h-5" data-id="element-9039" data-mp="17rfhxqf8par8" />
            <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-ink-faint" data-id="element-9040" data-mp="1cp7b0nrmf2x0">Giga inside ChatGPT</span>
          </div>
          <h1 className="font-title font-extrabold text-4xl md:text-[56px] leading-[1.03] tracking-tight max-w-[16ch]" data-id="element-9041" data-mp="39j0w7xe4g5tq">
            An entire universe inside ChatGPT.
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty max-w-[58ch]" data-id="element-9042" data-mp="1k86t8q3cioes">
            It looks like one chat box. Behind it sits everything your company runs on —
            every tool, team, credential, and skill — all working together the moment you
            type <strong className="text-brand font-semibold" data-id="element-9043" data-mp="1oz6ozs0qb0fp">@Giga</strong>.
          </p>

          {/* Visual: one message → the whole system unfolding beneath it */}
          <div className="w-full flex flex-col items-center mt-4 md:mt-6" data-id="element-9044" data-mp="11bxf62sg1xus">
            {/* ChatGPT-style composer */}
            <div className="w-full max-w-[620px] bg-white border border-sand-200 rounded-[26px] shadow-[0_10px_44px_rgba(43,33,26,0.09)] py-4 pl-5 pr-3 flex items-center gap-3 text-left" data-id="element-9045" data-mp="1b80hb8napwhq">
              <div className="flex-1 text-[15px] md:text-[16px] leading-snug text-ink" data-id="element-9046" data-mp="2mngr070ilo6t">
                <span className="inline-flex items-center gap-1.5 font-semibold text-brand-dark align-[-4px] mr-1.5" data-id="element-9047" data-mp="2mp9y1nf1iafc">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px]" data-id="element-9048" data-mp="3gutlosfn8k5" />@Giga
                </span>
                what's going on across the company today?
                <span className="inline-block w-[2px] h-[17px] bg-ink/60 align-[-3px] ml-0.5 animate-pulse" data-id="element-9049" data-mp="8dbg6ejzhev4" />
              </div>
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-9050" data-mp="3vjvow2jtjrft"><ArrowUp className="w-4 h-4" data-id="element-9051" data-mp="n4bydbaj8n4g" /></span>
            </div>

            {/* Connector down into the box */}
            <div className="w-0 h-9 md:h-10 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-9052" data-mp="r4vwoi6udws6" />

            {/* The universe box */}
            <div className="relative w-full border-2 border-dashed border-[#D6CCC0] rounded-[28px] bg-sand-50 pt-11 px-4 md:px-8 pb-7 md:pb-8" data-id="element-9053" data-mp="dcoebvgb28d0">
              <div className="absolute -top-3.5 left-5 md:left-8 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-9054" data-mp="162wmqvl1heee">
                <PulseDot data-id="element-9055" data-mp="1avpr3iclikpy" />
                <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-9056" data-mp="1avzm5kchzi2i">The universe behind the chat</span>
              </div>
              <div className="absolute -top-3 right-5 md:right-8 hidden sm:block font-mono text-[11px] tracking-[0.06em] uppercase text-ink-faint bg-cream px-2.5" data-id="element-9057" data-mp="144g535pfunfa">One shared context</div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-id="element-9058" data-mp="u6jwakjz44kc">
                {UNIVERSE.map(({
                icon: Icon,
                name,
                desc
              }) => <div key={name} className="bg-white border border-sand-200 rounded-2xl p-3.5 md:p-4 flex flex-col gap-2 text-left" data-id="element-9059" data-mp="l7osff4ej6k7">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark" data-id="element-9060" data-mp="6jk51xuem6v3"><Icon className="w-[18px] h-[18px]" data-id="element-9061" data-mp="ber6ez07azgu" /></span>
                    <span className="font-title font-bold text-[14.5px] leading-tight" data-id="element-9062" data-mp="3tni157shrdqp">{name}</span>
                    <span className="text-[12px] leading-[1.45] text-ink-muted" data-id="element-9063" data-mp="2eubifn7x4dc">{desc}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* ── 01 · Connect far more tools ───────────────────────── */}
        <Panel n="01" eyebrow="Integrations" title="Connect (far) more tools" blurb="ChatGPT reaches a handful of connectors. Giga plugs into 3,200+ tools with managed OAuth — and reads and writes, not just reads." data-id="element-9064" data-mp="3o0fzjhrrfo8m">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8" data-id="element-9065" data-mp="1sogxc24xh4w">
            {/* Coverage table */}
            <div className="rounded-2xl border border-sand-200 bg-cream p-5 md:p-6 flex flex-col gap-4" data-id="element-9066" data-mp="3t0aqflbjcdto">
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 pb-1" data-id="element-9067" data-mp="bhzqezsxontb">
                <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9068" data-mp="6jog6nyq8bak">Category</span>
                <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint text-right" data-id="element-9069" data-mp="kkhc9d4ofefs">ChatGPT</span>
                <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-brand-dark text-right" data-id="element-9070" data-mp="fmftktb5xh4u">Giga</span>
              </div>
              {COVERAGE.map(row => <div key={row.category} className="grid grid-cols-1 md:grid-cols-[110px_1fr] items-center gap-2 md:gap-4 py-1" data-id="element-9071" data-mp="3ecqszmbdm84n">
                  <span className="text-[13.5px] font-semibold text-ink" data-id="element-9072" data-mp="3ibzlyoyle1z5">{row.category}</span>
                  <div className="flex flex-col gap-1.5" data-id="element-9073" data-mp="3n5k91lyndbjk">
                    <CoverageBar value={row.chatgpt} data-id="element-9074" data-mp="3s3lv8frddqz9" />
                    <CoverageBar value={row.giga} giga data-id="element-9075" data-mp="3s56om21y73vj" />
                  </div>
                </div>)}
              <span className="text-[12px] text-ink-faint pt-1" data-id="element-9076" data-mp="yvu6org11d6">Share of tools in each category Giga can act inside.</span>
            </div>

            {/* Connect chat example */}
            <div className="flex flex-col gap-3" data-id="element-9077" data-mp="9wxroubyxbvp">
              <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9078" data-mp="27nilslpf16td">One line to connect</span>
              <div className="bg-white border border-[#E6E3DD] rounded-2xl shadow-[0_4px_24px_rgba(43,33,26,0.06)] overflow-hidden flex flex-col flex-1" data-id="element-9079" data-mp="2bpvof18v8olx">
                <div className="flex items-center gap-2.5 py-3 px-4 border-b border-[#F0EEE9]" data-id="element-9080" data-mp="2475tufupqy6o">
                  <img src={OPENAI_ICON} alt="" className="w-[15px] h-[15px]" data-id="element-9081" data-mp="1zcrwi7whqgzd" />
                  <span className="text-[13px] font-medium text-[#40403C]" data-id="element-9082" data-mp="1ufrqfwzn5e89">ChatGPT</span>
                  <span className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-[#8C8880]" data-id="element-9083" data-mp="1qfhn6s38h1x3">
                    <img src={GIGA_MARK} alt="" className="w-[13px] h-[13px] rounded-[3px]" data-id="element-9084" data-mp="2n3e3dvurdv7s" />Giga
                  </span>
                </div>
                <div className="p-5 flex flex-col gap-4 flex-1" data-id="element-9085" data-mp="2e2zigxlpii4t">
                  <div className="self-end max-w-[90%] bg-[#0D0D0D] rounded-[22px] py-3 px-[18px] text-[14.5px] leading-[1.5] text-white" data-id="element-9086" data-mp="2e5lo2rp1enke">
                    <span className="inline-flex items-center gap-1.5 mr-1.5 align-[-3px]" data-id="element-9087" data-mp="12ovgp58vnpp7">
                      <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px]" data-id="element-9088" data-mp="xopui4ldmvei" />
                      <strong data-id="element-9089" data-mp="2lm716kfnm0ak">Giga</strong>
                    </span>
                    connect our HubSpot and pull this quarter's pipeline
                  </div>
                  <div className="text-[14.5px] leading-[1.6] text-[#1A1A18] flex flex-col gap-3" data-id="element-9090" data-mp="2qeqj1tldjhl6">
                    <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-brand-dark" data-id="element-9091" data-mp="2cj60iz9k51ea">
                      <Check className="w-4 h-4" data-id="element-9092" data-mp="34cf34z7makp6" /> Connected via OAuth — no API keys to paste.
                    </span>
                    <span data-id="element-9093" data-mp="39c1h5av9s4z1">Pulled <strong data-id="element-9094" data-mp="39brqrm6bbvst">214 open deals</strong> worth <strong data-id="element-9095" data-mp="39aqcl8u02t6v">$1.2M</strong>. Northwind and two others have gone quiet — want me to draft the follow-ups?</span>
                  </div>
                  <div className="mt-auto border border-[#E6E3DD] rounded-[22px] py-3 pl-4 pr-3 flex items-center gap-3" data-id="element-9096" data-mp="30abok1pnizzo">
                    <span className="flex-1 text-[14px] text-[#A8A29A]" data-id="element-9097" data-mp="1jvjtti519gn7">Ask anything</span>
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0D0D0D] text-white" data-id="element-9098" data-mp="1ovy313idewmt"><ArrowUp className="w-4 h-4" data-id="element-9099" data-mp="1gdmfwb230xe9" /></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Panel>

        {/* ── 02 · Sharing, done right ──────────────────────────── */}
        <Panel n="02" eyebrow="Teams & permissions" title="Sharing, done right" blurb="Each team gets its own view of the brain, with access that mirrors the org chart. Set it once — it stays current as people and channels change." data-id="element-9100" data-mp="1qar22avxgsv2">
          <div className="rounded-2xl border border-sand-200 overflow-hidden" data-id="element-9101" data-mp="splc1ffum556">
            <div className="hidden md:grid grid-cols-[1.2fr_0.6fr_1.6fr_0.9fr] bg-cream border-b border-sand-200" data-id="element-9102" data-mp="17dzbrhhyxdhi">
              {['Group', 'Members', 'Connected tools', 'Access'].map(h => <div key={h} className="py-3 px-5 font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9103" data-mp="12ly1200g7utl">{h}</div>)}
            </div>
            {GROUPS.map((g, i) => <div key={g.name} className={['md:grid md:grid-cols-[1.2fr_0.6fr_1.6fr_0.9fr] md:items-center p-4 md:p-0', i === GROUPS.length - 1 ? '' : 'border-b border-sand-100'].join(' ')} data-id="element-9104" data-mp="9xk65t4npem9" data-mpi={i}>
                <div className="md:py-4 md:px-5 flex items-center gap-2.5" data-id="element-9105" data-mp="2ez4n5y25p89l">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand/10 text-brand-dark" data-id="element-9106" data-mp="2jqwhmjofny50"><Users className="w-4 h-4" data-id="element-9107" data-mp="2nsrg7gezbxf3" /></span>
                  <span className="font-title font-bold text-[15px]" data-id="element-9108" data-mp="2nrgbnedpmlmx">{g.name}</span>
                </div>
                <div className="md:py-4 md:px-5 text-[13.5px] text-ink-muted mt-1 md:mt-0" data-id="element-9109" data-mp="1r41ypr79l6tn">{g.members} members</div>
                <div className="md:py-4 md:px-5 text-[13.5px] text-ink-muted mt-1 md:mt-0" data-id="element-9110" data-mp="1w2wo5bzqltmq">{g.tools}</div>
                <div className="md:py-4 md:px-5 mt-2 md:mt-0" data-id="element-9111" data-mp="251qkr2d2ik0a">
                  <span className={['inline-block text-[11px] font-semibold rounded-full py-1 px-2.5', g.access === 'Read-only' ? 'bg-sand-100 text-ink-muted' : 'bg-brand/10 text-brand-dark'].join(' ')} data-id="element-9112" data-mp="2531myxqv7jpa">{g.access}</span>
                </div>
              </div>)}
          </div>
          <p className="text-[13px] text-ink-faint -mt-4" data-id="element-9113" data-mp="3qjsm96x77kkv">Leadership is read-only by design. Ask Giga to change access and it raises an approval request instead of doing it quietly.</p>
        </Panel>

        {/* ── 03 · Shared, inspectable memory ───────────────────── */}
        <Panel n="03" eyebrow="Company memory" title="Shared, inspectable company memory" blurb="One brain the whole company teaches and reads. Everything Giga knows is out in the open — browse it, correct it, and watch what it does with it." data-id="element-9114" data-mp="3ctyxeolp10b5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8" data-id="element-9115" data-mp="32tokz67hfwx1">
            {/* Memory explorer */}
            <div className="rounded-2xl border border-sand-200 bg-cream p-5 md:p-6 flex flex-col gap-3" data-id="element-9116" data-mp="2xy96qd5kbz03">
              <div className="flex items-center justify-between" data-id="element-9117" data-mp="2two19cvcymzx">
                <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9118" data-mp="w3ie9lzlbmgn">What Giga knows</span>
                <span className="font-mono text-[10px] text-ink-faint" data-id="element-9119" data-mp="s3zp2e0qkp80">3 of 1,204</span>
              </div>
              {MEMORY.map(m => <div key={m.tag} className="bg-white border border-sand-200 rounded-xl p-4 flex flex-col gap-1.5" data-id="element-9120" data-mp="1fdjuaerlffmf">
                  <span className="inline-block self-start text-[10px] font-semibold uppercase tracking-[0.08em] rounded-full py-1 px-2 bg-brand/10 text-brand-dark" data-id="element-9121" data-mp="11meth9chohai">{m.tag}</span>
                  <span className="text-[13.5px] leading-[1.5] text-ink" data-id="element-9122" data-mp="16iwvi1gfq1j5">{m.fact}</span>
                </div>)}
            </div>
            {/* Activity feed */}
            <div className="rounded-2xl border border-sand-200 bg-cream p-5 md:p-6 flex flex-col gap-3" data-id="element-9123" data-mp="won00aw74n47">
              <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9124" data-mp="iovfrsf36ahr">Feed</span>
              {FEED.map((f, i) => <div key={i} className="flex items-start gap-3" data-id="element-9125" data-mp="no8aezc78luw" data-mpi={i}>
                  <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-ink flex-none overflow-hidden" data-id="element-9126" data-mp="2d1v1m3a280xh">
                    {f.who === 'Giga' ? <img src={GIGA_MARK} alt="" className="w-full h-full" data-id="element-9127" data-mp="2s3tqen1k3x9a" /> : <span className="text-white text-[11px] font-bold" data-id="element-9128" data-mp="2s28vuxf8sebf">{f.who[0]}</span>}
                  </span>
                  <div className="flex flex-col gap-0.5" data-id="element-9129" data-mp="2n3fid4zygnb9">
                    <span className="text-[13.5px] leading-[1.45] text-ink" data-id="element-9130" data-mp="30xghw0yqeeco"><strong className="font-semibold" data-id="element-9131" data-mp="2w66vrace5t5j">{f.who}</strong> {f.text}</span>
                    <span className="font-mono text-[11px] text-ink-faint" data-id="element-9132" data-mp="2w43ufu5sgdli">{f.when}</span>
                  </div>
                </div>)}
            </div>
          </div>
        </Panel>

        {/* ── 04 · More tools ───────────────────────────────────── */}
        <Panel n="04" eyebrow="Beyond chat" title="More than a chat box" blurb="Giga keeps working when the tab is closed. Standing jobs, scheduled runs, and a place for everything it needs to do them." data-id="element-9133" data-mp="3ju80072jenf5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-9134" data-mp="1pi9sc9jvlzld">
            {MORE_TOOLS.map(({
            icon: Icon,
            name,
            desc
          }) => <div key={name} className="rounded-2xl border border-sand-200 bg-cream p-6 flex flex-col gap-3" data-id="element-9135" data-mp="st6ax3v0n2n0">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand/10 text-brand-dark" data-id="element-9136" data-mp="nttqeprma7nl"><Icon className="w-5 h-5" data-id="element-9137" data-mp="12ig30jpv0lb4" /></span>
                <span className="font-title font-bold text-lg" data-id="element-9138" data-mp="xkobhqt4n17g">{name}</span>
                <p className="text-[14px] leading-[1.55] text-ink-muted text-pretty" data-id="element-9139" data-mp="xlg5ix72sqvm">{desc}</p>
              </div>)}
          </div>
        </Panel>

        {/* ── 05 · No lock-in ───────────────────────────────────── */}
        <Panel n="05" eyebrow="Open by default" title="All the benefits, none of the lock-in" blurb="ChatGPT is a great front door — not a cage. Giga stays portable across models and surfaces, and your data stays yours." dark data-id="element-9140" data-mp="4ybw8g0y9qe1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-9141" data-mp="1uae1ujuh4wuw">
            {NO_LOCK_IN.map(({
            icon: Icon,
            name,
            desc
          }) => <div key={name} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 flex items-start gap-4" data-id="element-9142" data-mp="1qbolcie52z71">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 text-brand-lighter flex-none" data-id="element-9143" data-mp="22sh4ozcb8oi8"><Icon className="w-5 h-5" data-id="element-9144" data-mp="1nvqftk09iv6v" /></span>
                <div className="flex flex-col gap-1.5" data-id="element-9145" data-mp="1nx1hnea5dsvc">
                  <span className="font-title font-bold text-lg text-white" data-id="element-9146" data-mp="1sujiehe0h1s9">{name}</span>
                  <p className="text-[14px] leading-[1.55] text-white/70 text-pretty" data-id="element-9147" data-mp="2gkdu2vmlgkax">{desc}</p>
                </div>
              </div>)}
          </div>
          <div className="flex items-center gap-2.5 text-white/60" data-id="element-9148" data-mp="2lk9zmzaxwjsl">
            <ShieldCheck className="w-4 h-4 text-brand-lighter" data-id="element-9149" data-mp="26szzn29i2aon" />
            <span className="text-[13px]" data-id="element-9150" data-mp="2brughynwpp64">No proprietary formats. No exit tax. Leave any time with everything.</span>
          </div>
        </Panel>

      </div>
    </div>;
}